import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE } from '~/lib/api';
import { Icon, Tone } from '~/components/ui';
import { useT } from '~/i18n';
import {
  applyManual,
  fetchManualPreview,
  type JobForYou,
  type ManualQuota,
  type PreviewState,
} from '../api/freeTierClient';
import { formatStartDate } from '../lib/jobsForYou';
import type { PlanState } from '../lib/plan';
import { usePlanCta } from '../lib/planCta';

/**
 * La hoja de postular a mano, una vacante a la vez.
 *
 * Muestra la plantilla TAL COMO SALE (`GET /apply-manual/preview`, que no
 * guarda ni gasta cupo): genérica, correcta y en inglés, armada con campos del
 * perfil, sin modelo. Debajo, «La carta del plan» dice lo que el plan haría con
 * esta misma vacante, también con campos: no se escribe ninguna carta para
 * enseñarla, así el aviso no cuesta inferencia.
 *
 * Con Gmail el botón envía desde su Gmail. Sin Gmail (canal de dominio) copia el
 * texto, muestra la dirección del empleador y registra la postulación con
 * `mode: 'copy'`: el canal de dominio de TBit queda para el plan, porque ahí la
 * reputación que se arriesga es la nuestra.
 */
interface Props {
  job: JobForYou;
  quota: ManualQuota;
  /** Gmail conectado (`has_gmail` de onboarding-status). Sin él, se copia. */
  hasGmail: boolean;
  plan: PlanState;
  onClose: () => void;
  /** Salió (o se registró la copia): la fila pasa a «Postulaste hoy». */
  onApplied: (jobId: string) => void;
  /** El empleador ya dijo que no hay cupo: la fila pasa a cerrada. */
  onClosedJob: (jobId: string) => void;
  /** El servidor dijo cómo va el cupo (un 429 trae el suyo). */
  onQuota: (quota: ManualQuota) => void;
  /**
   * Salió una: el cupo suma uno. Lo suma quien guarda el cupo, sobre su valor
   * de ese momento, y no esta hoja con el que tenía antes del envío.
   */
  onUsedOne: () => void;
  /** El botón del plan: abre la oferta (surface 'manual_sheet'). */
  onPlan: () => void;
  /**
   * El servidor pidió los términos (409 `terms_required`): quien abrió la hoja
   * muestra el paso de consentimiento.
   */
  onTerms: () => void;
}

type Outcome =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent' }
  | { kind: 'copied'; to: string; subject: string; body: string; clipboard: boolean }
  | { kind: 'daily_limit'; limit: number }
  | { kind: 'closed' }
  | { kind: 'not_active' }
  | { kind: 'no_email' }
  | { kind: 'terms_required' }
  | { kind: 'paused' }
  | { kind: 'already_applied' }
  | { kind: 'gmail_reconnect' }
  | { kind: 'cv_required' }
  | { kind: 'failed' };

/** El texto que se pega en el correo: asunto arriba, cuerpo debajo. */
function mailText(subject: string, body: string): string {
  return `${subject}\n\n${body}`;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function ManualApplySheet({
  job,
  quota,
  hasGmail,
  plan,
  onClose,
  onApplied,
  onClosedJob,
  onQuota,
  onUsedOne,
  onPlan,
  onTerms,
}: Props) {
  const t = useT('workspace').dashboard;
  const ts = t.manualSheet;
  const cta = usePlanCta(plan);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [outcome, setOutcome] = useState<Outcome>(
    quota.used >= quota.limit ? { kind: 'daily_limit', limit: quota.limit } : { kind: 'idle' },
  );

  useEffect(() => {
    let cancelled = false;
    fetchManualPreview(job._id).then((p) => {
      if (cancelled) return;
      setPreview(p);
      if (p.status === 'job_closed') {
        setOutcome({ kind: 'closed' });
        onClosedJob(job._id);
      }
      if (p.status === 'job_not_active') setOutcome({ kind: 'not_active' });
      if (p.status === 'no_contact_email') setOutcome({ kind: 'no_email' });
    });
    return () => {
      cancelled = true;
    };
    // Una vista previa por vacante abierta; los callbacks no la vuelven a pedir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job._id]);

  // El foco entra al diálogo al abrir y vuelve a lo que lo abrió al cerrar.
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    boxRef.current?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const planActive = quota.plan_active || plan.plan_active;

  const handleSend = async () => {
    setOutcome({ kind: 'sending' });
    const mode = hasGmail ? 'gmail' : 'copy';
    const result = await applyManual(job._id, mode);
    switch (result.kind) {
      case 'sent':
        setOutcome({ kind: 'sent' });
        onApplied(job._id);
        onUsedOne();
        return;
      case 'copied': {
        const clipboard = await copyToClipboard(mailText(result.subject, result.body));
        setOutcome({ kind: 'copied', to: result.to, subject: result.subject, body: result.body, clipboard });
        onApplied(job._id);
        onUsedOne();
        return;
      }
      case 'daily_limit':
        setOutcome({ kind: 'daily_limit', limit: result.quota.limit });
        onQuota(result.quota);
        return;
      case 'job_closed':
        setOutcome({ kind: 'closed' });
        onClosedJob(job._id);
        return;
      case 'already_applied':
        setOutcome({ kind: 'already_applied' });
        onApplied(job._id);
        return;
      case 'job_not_active':
        setOutcome({ kind: 'not_active' });
        return;
      case 'no_contact_email':
        setOutcome({ kind: 'no_email' });
        return;
      case 'terms_required':
        setOutcome({ kind: 'terms_required' });
        return;
      case 'sending_paused':
        setOutcome({ kind: 'paused' });
        return;
      case 'gmail_reconnect':
        setOutcome({ kind: 'gmail_reconnect' });
        return;
      case 'cv_required':
        setOutcome({ kind: 'cv_required' });
        return;
      default:
        setOutcome({ kind: 'failed' });
    }
  };

  const done = outcome.kind === 'sent' || outcome.kind === 'copied' || outcome.kind === 'already_applied';
  const blocked =
    outcome.kind === 'daily_limit' ||
    outcome.kind === 'closed' ||
    outcome.kind === 'not_active' ||
    outcome.kind === 'no_email' ||
    outcome.kind === 'paused' ||
    done;
  const ready = preview?.status === 'ok';
  const hints = preview?.status === 'ok' ? preview.hints : null;
  const startDate = formatStartDate(hints?.start_date ?? job.start_date, 'long');
  // La que se está por mandar ya cuenta: «14 de 20» con 13 usadas.
  const countAfter = Math.min(quota.used + 1, quota.limit);

  return (
    <div className="modal modal-open modal-bottom sm:modal-middle" role="dialog" aria-modal="true" aria-label={ts.label}>
      <div ref={boxRef} tabIndex={-1} className="modal-box space-y-3 outline-none" style={{ maxWidth: '34rem' }}>
        <div className="flex items-center justify-between gap-2">
          <span className="brand-label">{ts.label}</span>
          <Tone tone="info">{ts.template}</Tone>
        </div>
        <p className="font-medium">
          {job.company} · {job.title}
        </p>
        <p className="text-xs hush">{hasGmail ? ts.noteGmail : ts.noteCopy}</p>

        {!preview && (
          <p className="text-sm hush flex items-center gap-2">
            <span className="loading loading-spinner loading-xs" />
            {ts.loading}
          </p>
        )}
        {preview?.status === 'error' && (
          <p className="text-sm" style={{ color: 'var(--coral-ink)' }}>
            {ts.previewFailed}
          </p>
        )}

        {preview?.status === 'ok' && (
          <div
            className="p-3 rounded-2xl text-xs whitespace-pre-wrap break-words max-h-64 overflow-y-auto"
            style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline)' }}
          >
            <p>
              {ts.to} {preview.to}
            </p>
            <p className="mb-2">
              {ts.subject} {preview.subject}
            </p>
            <p>{preview.body}</p>
          </div>
        )}

        {ready && planActive && (
          <div className="p-3 rounded-2xl text-xs" style={{ background: 'var(--surface-hi)' }}>
            <span>
              {ts.quotaLine(countAfter, quota.limit)} {ts.quotaShared}
            </span>
          </div>
        )}

        {ready && !planActive && (
          <div
            className="p-3 rounded-2xl space-y-2"
            style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline-hi)' }}
          >
            <p className="text-xs font-semibold" style={{ color: 'var(--purple-ink)' }}>
              {ts.planTitle}
            </p>
            <ul className="text-xs list-disc pl-4 space-y-1" style={{ color: 'var(--ink-soft)' }}>
              <li>{ts.planCompany(hints?.company ?? job.company, startDate)}</li>
              <li>{ts.planYears(hints?.years ?? null)}</li>
              <li>{ts.planAuto}</li>
            </ul>
            <button type="button" className="btn btn-soft btn-primary btn-sm" onClick={onPlan}>
              {cta.label}
            </button>
            <p className="text-xs hush">{cta.note}</p>
          </div>
        )}

        {outcome.kind === 'closed' && <Tone tone="muted">{t.jobsForYou.closed}</Tone>}

        <button
          type="button"
          className="btn btn-primary btn-block gap-2"
          onClick={handleSend}
          disabled={!ready || blocked || outcome.kind === 'sending'}
        >
          {outcome.kind === 'sending' ? (
            <span className="loading loading-spinner loading-sm" />
          ) : (
            <Icon name={hasGmail ? 'send' : 'mail'} />
          )}
          {hasGmail ? ts.sendGmail : ts.sendCopy}
        </button>

        {outcome.kind === 'sent' && <p className="text-xs text-data text-center">{ts.sent}</p>}

        {outcome.kind === 'copied' && (
          <div className="space-y-2 text-xs">
            <p>{outcome.clipboard ? ts.copied : ts.copyFailed}</p>
            <p className="font-semibold break-all select-all">{outcome.to}</p>
            {!outcome.clipboard && (
              <textarea
                className="textarea textarea-bordered w-full text-xs"
                rows={6}
                readOnly
                value={mailText(outcome.subject, outcome.body)}
                onFocus={(e) => e.currentTarget.select()}
              />
            )}
            <a
              className="btn btn-ghost btn-sm gap-2"
              href={`mailto:${outcome.to}?subject=${encodeURIComponent(outcome.subject)}&body=${encodeURIComponent(outcome.body)}`}
            >
              <Icon name="arrow-right" />
              {ts.openMail}
            </a>
          </div>
        )}

        {outcome.kind === 'daily_limit' && <p className="text-xs text-center hush">{ts.dailyLimit(outcome.limit)}</p>}
        {outcome.kind === 'no_email' && <p className="text-xs text-center hush">{ts.noEmail}</p>}
        {outcome.kind === 'not_active' && <p className="text-xs text-center hush">{ts.notActive}</p>}
        {outcome.kind === 'paused' && <p className="text-xs text-center hush">{ts.paused}</p>}
        {outcome.kind === 'terms_required' && (
          <div className="alert text-sm" role="status">
            <span>{ts.termsRequired}</span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onTerms}>
              {ts.termsOpen}
            </button>
          </div>
        )}
        {outcome.kind === 'already_applied' && <p className="text-xs text-center hush">{ts.alreadyApplied}</p>}
        {outcome.kind === 'failed' && (
          <p className="text-xs text-center" style={{ color: 'var(--coral-ink)' }}>
            {ts.failed}
          </p>
        )}
        {outcome.kind === 'gmail_reconnect' && (
          /* El mismo aviso de reconectar de la bandeja: el flujo de conexión de siempre. */
          <div className="alert text-sm" role="status">
            <span>{ts.reconnect}</span>
            <a className="btn btn-ghost btn-sm gap-2" href={`${API_BASE}/job-apply/auth/start`}>
              <Icon name="refresh" />
              {t.reconnectGmail}
            </a>
          </div>
        )}
        {outcome.kind === 'cv_required' && (
          <div className="alert text-sm" role="status">
            <span>{ts.cvRequired}</span>
            <Link to="/onboarding?step=cv" className="btn btn-ghost btn-sm gap-2">
              <Icon name="doc" />
              {ts.cvLink}
            </Link>
          </div>
        )}

        <div className="modal-action mt-0">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            {ts.close}
          </button>
        </div>
      </div>
      {/* El fondo también cierra; fuera del árbol accesible para no repetir «Cerrar». */}
      <button className="modal-backdrop" aria-hidden="true" tabIndex={-1} onClick={onClose} />
    </div>
  );
}
